import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Alert, Button, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faAppleWhole,
    faBed,
    faClipboardCheck,
    faDroplet,
    faFaceSmile,
    faGaugeHigh,
    faHeartPulse,
    faImage,
    faPersonWalking,
    faUtensils,
    faWeightScale,
} from "@fortawesome/free-solid-svg-icons";
import { AppUserApi } from "../../../api";
import {
    ActivityTargetTrace,
    ActivityReport,
    CustomBreadCrumb,
    SleepReport,
    TrafficLight,
} from "../../../components";
import {
    AiGoalType,
    AppUserType,
    DailyUserSummaryType,
    InsightDay,
    PlanWeekType,
    ProgressReportType,
    WeeklyUserSummaryType,
} from "../../../DTO";
import { useAppParams } from "../../../Router/hook";
import { formatUserId } from "../../../utils";
import { setWeek } from "../../../redux-config/entities/week";
import { selectUser } from "../../../redux-config/entities/user";
import { useAppDispatch, useAppSelector } from "../../../redux-config/hooks";
import { DailySummarySection } from "../summary/components/daily-summary-section";
import { WeeklySummarySection } from "../summary/components/weekly-summary-section";
import { GoalCard } from "../summary/components/goal-card";
import "../summary/style.scss";
import "./style.scss";

type Tab = "overview" | "daily" | "nutrition" | "activity" | "sleep" | "other";
type OtherReport =
    | "engagement_detail"
    | "stress_detail"
    | "weight_detail"
    | "blood_detail";
const TABS = [
    { id: "overview", label: "Overview", icon: faGaugeHigh },
    { id: "daily", label: "Daily", icon: faClipboardCheck },
    { id: "nutrition", label: "Nutrition", icon: faAppleWhole },
    { id: "activity", label: "Activity", icon: faPersonWalking },
    { id: "sleep", label: "Sleep", icon: faBed },
    { id: "other", label: "Other health", icon: faHeartPulse },
];
const DAY_MARKERS = [
    { key: "food", label: "Food", icon: faUtensils },
    { key: "activity", label: "Activity", icon: faPersonWalking },
    { key: "sleep", label: "Sleep", icon: faBed },
    { key: "stress", label: "Mood", icon: faFaceSmile },
    { key: "weight", label: "Weight", icon: faWeightScale },
    { key: "blood_sugar", label: "Blood", icon: faDroplet },
] as const;
const dayCardTab = (key: string): Tab => {
    if (key === "nutrition" || key === "meal_pattern") return "nutrition";
    if (key === "physical_activity") return "activity";
    if (key === "sleep") return "sleep";
    return "other";
};
const elementAlignmentKey = (type?: string) => {
    if (
        ["breakfast", "lunch", "launch", "dinner", "snack", "food"].includes(
            type || "",
        )
    )
        return "nutrition";
    if (["activity", "physical_activity"].includes(type || ""))
        return "physical_activity";
    if (type === "sleep") return "sleep";
    if (["review", "day_review", "stress", "stress_mood"].includes(type || ""))
        return "mindset";
    return "";
};

const NUTRITION_UNITS: Record<string, string> = {
    calories: "kcal",
    protein: "g",
    carbohydrates: "g",
    fat: "g",
    fiber: "g",
    sugar: "g",
    saturated_fat: "g",
    trans_fat: "g",
    sodium: "mg",
    potassium: "mg",
    cholesterol: "mg",
};

const formatNutritionValue = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === "") return "—";
    return `${value}${NUTRITION_UNITS[key] ? ` ${NUTRITION_UNITS[key]}` : ""}`;
};

const FOOD_SCORE_COMPONENTS = [
    ["carbohydrate_balance", "Carbohydrate balance"],
    ["sugar_risk", "Sugar risk"],
    ["fiber", "Fiber"],
    ["protein", "Protein"],
] as const;

const OtherReports: FC<{
    userId: number;
    weekId: number;
    days: InsightDay[];
}> = ({ userId, weekId, days }) => {
    const user = useAppSelector(selectUser);
    const [reports, setReports] = useState<
        Partial<Record<OtherReport, ProgressReportType>>
    >({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [view, setView] = useState<"daily" | "weekly">("daily");
    useEffect(() => {
        const types: OtherReport[] = [
            "engagement_detail",
            "stress_detail",
            "weight_detail",
            "blood_detail",
        ];
        setLoading(true);
        setError("");
        Promise.all(
            types.map((type) =>
                AppUserApi.UserReportDetail(user, userId, weekId, type),
            ),
        )
            .then((responses) => {
                const next: Partial<Record<OtherReport, ProgressReportType>> =
                    {};
                types.forEach((type, index) => {
                    next[type] = responses[index].report;
                });
                setReports(next);
            })
            .catch((err) => setError(String(err || "Reports unavailable")))
            .finally(() => setLoading(false));
    }, [user, userId, weekId]);
    if (loading)
        return (
            <div className="insight-state">
                <Spinner size="sm" animation="border" /> Loading reports…
            </div>
        );
    if (error) return <Alert variant="danger">{error}</Alert>;
    const formatTime = (value: string) =>
        value ? value.replace("T", " ").slice(11, 16) : "";
    return (
        <div className="health-report-stack">
            <div
                className="health-view-tabs"
                role="tablist"
                aria-label="Health data period"
            >
                <button
                    type="button"
                    className={view === "daily" ? "is-active" : ""}
                    onClick={() => setView("daily")}
                >
                    Day by day
                </button>
                <button
                    type="button"
                    className={view === "weekly" ? "is-active" : ""}
                    onClick={() => setView("weekly")}
                >
                    Week summary
                </button>
            </div>
            {view === "daily" && (
                <section className="health-chart-panel">
                    <header>
                        <div>
                            <span>Daily records</span>
                            <h2>Mood, stress, weight and clinical readings</h2>
                        </div>
                        <p>
                            Each column shows what was actually recorded on that
                            day. Missing data is left blank, never shown as
                            zero.
                        </p>
                    </header>
                    <div className="health-daily-chart">
                        {days
                            .filter((day) => !day.isFuture)
                            .map((day) => (
                                <article key={day.id}>
                                    <strong>{day.name.slice(0, 3)}</strong>
                                    <small>{day.date}</small>
                                    <div
                                        className={
                                            day.measurements.stress?.length
                                                ? "has-data"
                                                : "no-data"
                                        }
                                    >
                                        <span>Mood & stress</span>
                                        <b>
                                            {day.measurements.stress?.length
                                                ? day.measurements.stress
                                                      .map(
                                                          (record) =>
                                                              `Mood ${record.mood ?? "—"}/5 · Stress ${record.stress ?? "—"}/5${formatTime(record.createdAt) ? ` (${formatTime(record.createdAt)})` : ""}`,
                                                      )
                                                      .join("; ")
                                                : "—"}
                                        </b>
                                    </div>
                                    <div
                                        className={
                                            day.measurements.weight?.length
                                                ? "has-data"
                                                : "no-data"
                                        }
                                    >
                                        <span>Weight & waist</span>
                                        <b>
                                            {day.measurements.weight?.length
                                                ? day.measurements.weight
                                                      .map(
                                                          (record) =>
                                                              `${record.weight ?? "—"} kg${record.waist != null && record.waist > 0 ? ` · ${record.waist} cm WC` : ""}${formatTime(record.createdAt) ? ` (${formatTime(record.createdAt)})` : ""}`,
                                                      )
                                                      .join("; ")
                                                : "—"}
                                        </b>
                                    </div>
                                    <div
                                        className={
                                            day.measurements.blood_sugar?.length
                                                ? "has-data"
                                                : "no-data"
                                        }
                                    >
                                        <span>Blood sugar</span>
                                        <b>
                                            {day.measurements.blood_sugar
                                                ?.length
                                                ? day.measurements.blood_sugar
                                                      .map(
                                                          (record) =>
                                                              `${record.bloodSugar ?? "—"} mmol/L${formatTime(record.createdAt) ? ` (${formatTime(record.createdAt)})` : ""}`,
                                                      )
                                                      .join("; ")
                                                : "—"}
                                        </b>
                                    </div>
                                    <div
                                        className={
                                            day.measurements.blood_pressure
                                                ?.length
                                                ? "has-data"
                                                : "no-data"
                                        }
                                    >
                                        <span>Blood pressure</span>
                                        <b>
                                            {day.measurements.blood_pressure
                                                ?.length
                                                ? day.measurements.blood_pressure
                                                      .map(
                                                          (record) =>
                                                              `${record.systolic ?? "—"}/${record.diastolic ?? "—"} mm Hg${formatTime(record.createdAt) ? ` (${formatTime(record.createdAt)})` : ""}`,
                                                      )
                                                      .join("; ")
                                                : "—"}
                                        </b>
                                    </div>
                                </article>
                            ))}
                    </div>
                    <p className="health-chart-note">
                        Select the Daily tab above for the complete logs and
                        measurements for a specific date.
                    </p>
                </section>
            )}
            {view === "weekly" && (
                <div className="insight-report-grid">
                    {(
                        Object.entries(reports) as Array<
                            [OtherReport, ProgressReportType]
                        >
                    ).map(([type, report]) => (
                        <section className="insight-panel" key={type}>
                            <h3>
                                {report.name ||
                                    type
                                        .replace("_detail", "")
                                        .replace("_", " ")}
                            </h3>
                            {report.pages
                                .flatMap((page) => page.body)
                                .map((body, bodyIndex) => (
                                    <div
                                        className="insight-report-block"
                                        key={`${body.title}-${bodyIndex}`}
                                    >
                                        <strong>{body.title}</strong>
                                        <Table
                                            size="sm"
                                            responsive
                                            className="mb-0 mt-2"
                                        >
                                            <tbody>
                                                {body.items.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.label}</td>
                                                        <td className="text-end fw-semibold">
                                                            {item.value}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ))}
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
};

export const UserInsights: FC = () => {
    const { id } = useAppParams();
    const userId = Number(id);
    const user = useAppSelector(selectUser);
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState<Tab>("overview");
    const [expandedDayCard, setExpandedDayCard] = useState("");
    const [userContext, setUserContext] = useState<AppUserType>();
    const [weeks, setWeeks] = useState<PlanWeekType[]>([]);
    const [selectedWeek, setSelectedWeek] = useState<PlanWeekType>();
    const [days, setDays] = useState<InsightDay[]>([]);
    const [selectedDay, setSelectedDay] = useState<InsightDay>();
    const [weeklySummary, setWeeklySummary] = useState<WeeklyUserSummaryType>();
    const [dailySummary, setDailySummary] = useState<DailyUserSummaryType>();
    const [goals, setGoals] = useState<AiGoalType[]>([]);
    const [loading, setLoading] = useState(true);
    const [sectionLoading, setSectionLoading] = useState(false);
    const [error, setError] = useState("");
    const crumbs = [
        { name: "", href: "" },
        { name: "Dashboard", href: "/" },
        { name: "User list", href: "/app-user" },
        { name: `User Insights ${formatUserId(userId)}`, href: "#" },
    ];

    useEffect(() => {
        setLoading(true);
        Promise.all([
            AppUserApi.ShowAppUser(user, userId),
            AppUserApi.ListPlanUserWeek(user, userId, false),
            AppUserApi.GetUserGoals(user, userId),
        ])
            .then(([userResult, weekResult, goalResult]) => {
                setUserContext(userResult.user);
                setGoals(goalResult.goals);
                const list = [...weekResult.plan].sort(
                    (a, b) => a.index - b.index,
                );
                setWeeks(list);
                const initial =
                    list.find((week) => week.isCurrentWeek) ??
                    list[list.length - 1];
                if (initial) {
                    setSelectedWeek(initial);
                    dispatch(setWeek(initial));
                }
            })
            .catch((err) =>
                setError(String(err || "User insights unavailable")),
            )
            .finally(() => setLoading(false));
    }, [dispatch, user, userId]);

    useEffect(() => {
        if (!selectedWeek?.id) return;
        setSectionLoading(true);
        setError("");
        dispatch(setWeek(selectedWeek));
        Promise.all([
            AppUserApi.GetUserInsightsAvailability(
                user,
                userId,
                selectedWeek.id,
            ),
            AppUserApi.getWeeklyUserSummary(
                user,
                userId,
                selectedWeek.id,
            ).catch(() => null),
        ])
            .then(([availability, weekly]) => {
                const nextDays = availability.insights.days;
                setDays(nextDays);
                setWeeklySummary(weekly?.summary);
                const current = nextDays.find(
                    (day) => day.isCurrentDay && !day.isFuture,
                );
                const newestWithData = [...nextDays]
                    .reverse()
                    .find((day) => day.hasAnyData && !day.isFuture);
                setSelectedDay(
                    current ??
                        newestWithData ??
                        [...nextDays].reverse().find((day) => !day.isFuture),
                );
            })
            .catch((err) => setError(String(err || "Week data unavailable")))
            .finally(() => setSectionLoading(false));
    }, [dispatch, selectedWeek, user, userId]);

    const fetchDaily = useCallback(() => {
        if (!selectedDay?.id) return;
        setSectionLoading(true);
        AppUserApi.getDailyUserSummary(user, userId, selectedDay.id)
            .then((result) => setDailySummary(result.summary))
            .catch(() => setDailySummary(undefined))
            .finally(() => setSectionLoading(false));
    }, [selectedDay?.id, user, userId]);
    useEffect(() => {
        fetchDaily();
    }, [fetchDaily]);

    const weekGoal = useMemo(
        () =>
            goals.find(
                (goal) =>
                    goal.type === "week" && goal.weekId === selectedWeek?.id,
            ),
        [goals, selectedWeek?.id],
    );
    const dayGoal = useMemo(
        () =>
            goals.find(
                (goal) => goal.type === "day" && goal.dayId === selectedDay?.id,
            ),
        [goals, selectedDay?.id],
    );
    const updateGoal = (updated: AiGoalType) =>
        setGoals((current) =>
            current.map((goal) => (goal.id === updated.id ? updated : goal)),
        );
    const fullName =
        `${userContext?.firstName || ""} ${userContext?.lastName || ""}`.trim() ||
        "Unknown user";
    const dayAlignment = dailySummary?.alignment_summary;
    const dayCounts = dayAlignment?.overall?.traffic_light_counts;

    return (
        <>
            <Helmet>
                <title>D2Type | User Insights</title>
            </Helmet>
            <div className="user-insights">
                <CustomBreadCrumb breadCrumbList={crumbs} />
                <header className="insight-header">
                    <div>
                        <span className="insight-kicker">User Insights</span>
                        <h1>{fullName}</h1>
                        <p>
                            {userContext?.displayUserId ||
                                formatUserId(
                                    userId,
                                    userContext?.createdAt,
                                )}{" "}
                            · {userContext?.email || "No email"}
                        </p>
                    </div>
                    <div className="insight-header-controls">
                        <label>
                            <span>Week</span>
                            <select
                                value={selectedWeek?.id ?? ""}
                                onChange={(event) =>
                                    setSelectedWeek(
                                        weeks.find(
                                            (week) =>
                                                week.id ===
                                                Number(event.target.value),
                                        ),
                                    )
                                }
                            >
                                {weeks.map((week) => (
                                    <option value={week.id} key={week.id}>
                                        Week {week.index}
                                        {week.isCurrentWeek ? " (Current)" : ""}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {days.length > 0 && (
                            <label>
                                <span>Day</span>
                                <select
                                    value={selectedDay?.id ?? ""}
                                    onChange={(event) => {
                                        const day = days.find(
                                            (item) =>
                                                item.id ===
                                                Number(event.target.value),
                                        );
                                        if (day) setSelectedDay(day);
                                    }}
                                >
                                    {days.map((day) => (
                                        <option
                                            key={day.id}
                                            value={day.id}
                                            disabled={day.isFuture}
                                        >
                                            {day.name}
                                            {day.isCurrentDay
                                                ? " (Current)"
                                                : ""}{" "}
                                            · {day.date}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() =>
                                selectedWeek &&
                                setSelectedWeek({ ...selectedWeek })
                            }
                        >
                            Refresh
                        </Button>
                    </div>
                </header>
                {error && (
                    <Alert variant="danger" className="mt-3">
                        {error}
                    </Alert>
                )}
                {loading ? (
                    <div className="insight-state">
                        <Spinner animation="border" size="sm" /> Loading user…
                    </div>
                ) : (
                    <>
                        <section
                            className="insight-timeline"
                            aria-labelledby="week-activity-title"
                        >
                            <div className="insight-section-title">
                                <div>
                                    <span className="insight-kicker">
                                        Selected week
                                    </span>
                                    <h2 id="week-activity-title">
                                        Daily data availability
                                    </h2>
                                </div>
                                <p>
                                    Select a day to inspect its records. Each
                                    label shows whether that data was recorded.
                                </p>
                            </div>
                            <div className="insight-day-strip">
                                {days.map((day) => (
                                    <button
                                        type="button"
                                        key={day.id}
                                        disabled={day.isFuture}
                                        onClick={() => {
                                            setSelectedDay(day);
                                            setActiveTab("daily");
                                            setExpandedDayCard("");
                                        }}
                                        className={`insight-day ${selectedDay?.id === day.id ? "is-selected" : ""} ${!day.hasAnyData ? "is-empty" : ""}`}
                                    >
                                        <span className="insight-day-header">
                                            <span>
                                                <strong>{day.name}</strong>
                                                <small>{day.date}</small>
                                            </span>
                                            {day.isCurrentDay && <em>Today</em>}
                                            {day.pendingGoalCount > 0 && (
                                                <em className="goal-review-needed">
                                                    Goal needs approval
                                                </em>
                                            )}
                                            <strong className="insight-day-percent">
                                                {day.percentage}%
                                            </strong>
                                        </span>
                                        <span className="insight-markers">
                                            {DAY_MARKERS.map((marker) => {
                                                const availability =
                                                    day.data[marker.key];
                                                return (
                                                    <span
                                                        key={marker.key}
                                                        className={
                                                            availability?.hasData
                                                                ? "has-data"
                                                                : ""
                                                        }
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={marker.icon}
                                                        />
                                                        <span>
                                                            {marker.label}
                                                        </span>
                                                        <small>
                                                            {availability?.hasData
                                                                ? availability.count
                                                                : "—"}
                                                        </small>
                                                    </span>
                                                );
                                            })}
                                        </span>
                                        <span className="insight-day-footer">
                                            {day.isFuture
                                                ? "Future day"
                                                : day.hasAnyData
                                                  ? `${day.percentage}% plan completion`
                                                  : "No records for this day"}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </section>
                        <section
                            className="insight-goals"
                            aria-labelledby="selected-goals-title"
                        >
                            <div className="insight-section-title">
                                <div>
                                    <span className="insight-kicker">
                                        Coach editable
                                    </span>
                                    <h2 id="selected-goals-title">
                                        Daily and weekly goals
                                    </h2>
                                </div>
                                <p>
                                    Daily goals are available immediately.
                                    Weekly goals remain reviewable before
                                    approval.
                                </p>
                            </div>
                            <div className="insight-goal-grid">
                                <div>
                                    <span className="insight-goal-scope">
                                        Selected week
                                    </span>
                                    {weekGoal ? (
                                        <GoalCard
                                            goal={weekGoal}
                                            onUpdated={updateGoal}
                                        />
                                    ) : (
                                        <p className="insight-empty">
                                            No goal generated for this week yet.
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <span className="insight-goal-scope">
                                        Selected day
                                    </span>
                                    {dayGoal ? (
                                        <GoalCard
                                            goal={dayGoal}
                                            onUpdated={updateGoal}
                                        />
                                    ) : (
                                        <p className="insight-empty">
                                            No goal generated for this day yet.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </section>
                        {selectedDay && (
                            <section
                                className="day-glance"
                                aria-labelledby="day-glance-title"
                            >
                                <header className="day-glance-header">
                                    <div>
                                        <span className="insight-kicker">
                                            Selected day
                                        </span>
                                        <h2 id="day-glance-title">
                                            {selectedDay.isCurrentDay
                                                ? "Today"
                                                : selectedDay.name}{" "}
                                            at a Glance
                                        </h2>
                                        <p>
                                            {selectedDay.name},{" "}
                                            {selectedDay.date}
                                        </p>
                                    </div>
                                    <div className="day-completion">
                                        <strong>
                                            {selectedDay.percentage}%
                                        </strong>
                                        <span>plan completion</span>
                                    </div>
                                </header>
                                <div className="day-glance-overview">
                                    <div>
                                        <span
                                            className={`traffic-signal is-${dayAlignment?.overall?.overall_status || "none"}`}
                                        >
                                            <i />
                                            <i />
                                            <i />
                                        </span>
                                        <div>
                                            <h3>Overall rating</h3>
                                            <p>
                                                On track{" "}
                                                <strong>
                                                    {dayCounts?.green ?? 0}
                                                </strong>
                                            </p>
                                            <p>
                                                Needs attention{" "}
                                                <strong>
                                                    {dayCounts?.amber ?? 0}
                                                </strong>
                                            </p>
                                            <p>
                                                Off track{" "}
                                                <strong>
                                                    {dayCounts?.red ?? 0}
                                                </strong>
                                            </p>
                                            <p>
                                                Not enough data{" "}
                                                <strong>
                                                    {dayCounts?.no_data ?? 0}
                                                </strong>
                                            </p>
                                        </div>
                                    </div>
                                    <aside>
                                        <span>Day goal</span>
                                        <strong>
                                            {dayGoal?.metricTarget ||
                                                dayGoal?.description ||
                                                "No day goal"}
                                        </strong>
                                        <p>
                                            {dayGoal?.recommendedAction ||
                                                "No recommended action has been generated for this day."}
                                        </p>
                                    </aside>
                                </div>
                                <div className="day-insight-grid">
                                    {(dayAlignment?.cards ?? []).map((card) => {
                                        const details = Object.entries({
                                            ...card.metrics,
                                            ...card.data_points,
                                        }).filter(
                                            ([, value]) =>
                                                value !== null &&
                                                value !== undefined &&
                                                value !== "",
                                        );
                                        const isExpanded =
                                            expandedDayCard === card.key;
                                        return (
                                            <article
                                                key={card.key}
                                                className={`day-insight-card status-${card.status || "no_data"}`}
                                            >
                                                <div className="day-insight-title">
                                                    <TrafficLight
                                                        status={card.status}
                                                        compact
                                                    />
                                                    <h3>{card.title}</h3>
                                                </div>
                                                {card.status !== "no_data" && (
                                                    <strong className="day-insight-score">
                                                        Score: {card.score}/100
                                                    </strong>
                                                )}
                                                <p>
                                                    {card.reason ||
                                                        "No recorded data for this category."}
                                                </p>
                                                {isExpanded && (
                                                    <div className="day-card-details">
                                                        {details.length ? (
                                                            details.map(
                                                                ([
                                                                    label,
                                                                    value,
                                                                ]) => (
                                                                    <span
                                                                        key={
                                                                            label
                                                                        }
                                                                    >
                                                                        <small>
                                                                            {label.replaceAll(
                                                                                "_",
                                                                                " ",
                                                                            )}
                                                                        </small>
                                                                        <strong>
                                                                            {typeof value ===
                                                                            "boolean"
                                                                                ? value
                                                                                    ? "Yes"
                                                                                    : "No"
                                                                                : String(
                                                                                      value,
                                                                                  )}
                                                                        </strong>
                                                                    </span>
                                                                ),
                                                            )
                                                        ) : (
                                                            <p>
                                                                No additional
                                                                recorded values
                                                                for this day.
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="day-card-actions">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setExpandedDayCard(
                                                                isExpanded
                                                                    ? ""
                                                                    : card.key,
                                                            )
                                                        }
                                                    >
                                                        {isExpanded
                                                            ? "Hide details"
                                                            : `See ${card.title.toLowerCase()} details`}
                                                    </button>
                                                    {isExpanded && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setActiveTab(
                                                                    dayCardTab(
                                                                        card.key,
                                                                    ),
                                                                )
                                                            }
                                                        >
                                                            Open full logs
                                                        </button>
                                                    )}
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                                {dayAlignment?.suggested_focus?.message && (
                                    <div className="day-focus">
                                        <strong>
                                            {dayAlignment.suggested_focus.title}
                                        </strong>
                                        <p>
                                            {
                                                dayAlignment.suggested_focus
                                                    .message
                                            }
                                        </p>
                                        <span>
                                            {
                                                dayAlignment.suggested_focus
                                                    .recommended_action
                                            }
                                        </span>
                                    </div>
                                )}
                            </section>
                        )}
                        <nav
                            className="insight-tabs"
                            role="tablist"
                            aria-label="Insight sections"
                        >
                            {TABS.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === tab.id}
                                    onClick={() => setActiveTab(tab.id as Tab)}
                                    className={
                                        activeTab === tab.id ? "is-active" : ""
                                    }
                                >
                                    <FontAwesomeIcon icon={tab.icon} />
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                        {sectionLoading && (
                            <div className="insight-state compact">
                                <Spinner animation="border" size="sm" /> Loading
                                selected data…
                            </div>
                        )}
                        {!sectionLoading && activeTab === "overview" && (
                            <div className="insight-stack">
                                {weeklySummary ? (
                                    <WeeklySummarySection
                                        hasWeekSelection={Boolean(selectedWeek)}
                                        selectedWeekId={selectedWeek?.id}
                                        loading={false}
                                        error=""
                                        summary={weeklySummary}
                                        onRetry={() =>
                                            selectedWeek &&
                                            setSelectedWeek({ ...selectedWeek })
                                        }
                                        weekGoal={weekGoal}
                                        onGoalUpdated={updateGoal}
                                        daysWithRecords={
                                            days.filter((day) => day.hasAnyData)
                                                .length
                                        }
                                        totalWeekDays={days.length || 7}
                                    />
                                ) : (
                                    <Alert variant="light">
                                        This week could not be loaded. Refresh
                                        the selected week or check that the user
                                        has an active plan.
                                    </Alert>
                                )}
                                <ActivityTargetTrace
                                    userId={userId}
                                    compact
                                    selectedWeekIndex={selectedWeek?.index}
                                />
                            </div>
                        )}
                        {!sectionLoading &&
                            activeTab === "daily" &&
                            selectedDay && (
                                <div className="insight-stack">
                                    <div className="insight-panel insight-day-meta">
                                        <strong>
                                            {selectedDay.name},{" "}
                                            {selectedDay.date}
                                        </strong>
                                        <span>
                                            {selectedDay.planElements.length}{" "}
                                            plan elements
                                        </span>
                                        <span>
                                            {selectedDay.hasAnyData
                                                ? "Recorded data available"
                                                : "No recorded data"}
                                        </span>
                                    </div>
                                    <section className="insight-panel">
                                        <div className="insight-panel-heading">
                                            <h2>Plan elements</h2>
                                            <span>
                                                Completion by recorded data
                                            </span>
                                        </div>
                                        <div className="insight-element-list">
                                            {selectedDay.planElements.map(
                                                (element) => {
                                                    const elementLight =
                                                        dayAlignment?.cards.find(
                                                            (card) =>
                                                                card.key ===
                                                                elementAlignmentKey(
                                                                    element.type,
                                                                ),
                                                        )?.status;
                                                    return (
                                                        <div
                                                            key={
                                                                element.dailyTaskId
                                                            }
                                                        >
                                                            <span>
                                                                <strong>
                                                                    {
                                                                        element.title
                                                                    }
                                                                </strong>
                                                                <small>
                                                                    {element.type ||
                                                                        "task"}
                                                                </small>
                                                            </span>
                                                            <TrafficLight
                                                                status={
                                                                    elementLight
                                                                }
                                                                compact
                                                                label={
                                                                    elementLight &&
                                                                    elementLight !==
                                                                        "no_data"
                                                                        ? elementLight.replace(
                                                                              "amber",
                                                                              "Needs attention",
                                                                          )
                                                                        : element.hasData
                                                                          ? "Recorded; rating pending"
                                                                          : "Not recorded"
                                                                }
                                                            />
                                                        </div>
                                                    );
                                                },
                                            )}
                                        </div>
                                    </section>
                                    {dailySummary ? (
                                        <DailySummarySection
                                            hasWeekSelection={Boolean(
                                                selectedWeek,
                                            )}
                                            hasDaySelection
                                            selectedDayId={selectedDay.id}
                                            loading={false}
                                            error=""
                                            summary={dailySummary}
                                            onRetry={fetchDaily}
                                            dayGoal={dayGoal}
                                            onGoalUpdated={updateGoal}
                                        />
                                    ) : (
                                        <Alert variant="light">
                                            No generated daily summary is
                                            available. The availability markers
                                            above still show the raw logs
                                            recorded for this day.
                                        </Alert>
                                    )}
                                </div>
                            )}
                        {!sectionLoading && activeTab === "nutrition" && (
                            <section className="insight-panel">
                                <div className="insight-panel-heading">
                                    <div>
                                        <span className="insight-kicker">
                                            Selected day
                                        </span>
                                        <h2>Foods eaten</h2>
                                    </div>
                                    <span>
                                        {dailySummary?.food_logs?.length ?? 0}{" "}
                                        meals
                                    </span>
                                </div>
                                {!dailySummary?.food_logs?.length ? (
                                    <p className="insight-empty">
                                        No food data for this day.
                                    </p>
                                ) : (
                                    <div className="insight-meal-list">
                                        {dailySummary.food_logs.map((meal) => {
                                            const primaryImage =
                                                meal.attachments?.[0];
                                            return (
                                                <article
                                                    className="insight-meal"
                                                    key={meal.id}
                                                >
                                                    <div className="insight-meal-photo">
                                                        {primaryImage?.url ? (
                                                            <a
                                                                href={
                                                                    primaryImage.url
                                                                }
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                <img
                                                                    src={
                                                                        primaryImage.url
                                                                    }
                                                                    alt={`${meal.type} uploaded by the user`}
                                                                />
                                                            </a>
                                                        ) : (
                                                            <span>
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faImage
                                                                    }
                                                                />
                                                                <small>
                                                                    No image
                                                                </small>
                                                            </span>
                                                        )}
                                                        {meal.attachments
                                                            ?.length > 1 && (
                                                            <em>
                                                                +
                                                                {meal
                                                                    .attachments
                                                                    .length - 1}
                                                            </em>
                                                        )}
                                                    </div>
                                                    <div className="insight-meal-content">
                                                        <div className="insight-meal-heading">
                                                            <div>
                                                                <span className="insight-kicker">
                                                                    {meal.created_at
                                                                        ?.replace(
                                                                            "T",
                                                                            " ",
                                                                        )
                                                                        .slice(
                                                                            0,
                                                                            16,
                                                                        ) ||
                                                                        "Time unavailable"}
                                                                </span>
                                                                <h3>
                                                                    {meal.type ===
                                                                    "launch"
                                                                        ? "Lunch"
                                                                        : meal.type}
                                                                </h3>
                                                            </div>
                                                            <div>
                                                                <TrafficLight
                                                                    status={
                                                                        meal
                                                                            .traffic_light
                                                                            ?.overall
                                                                            ?.light
                                                                    }
                                                                    compact
                                                                    label={
                                                                        meal
                                                                            .traffic_light
                                                                            ?.overall
                                                                            ?.light
                                                                            ? "Meal rating"
                                                                            : "Not rated"
                                                                    }
                                                                />
                                                                <strong>
                                                                    {meal
                                                                        .traffic_light
                                                                        ?.overall
                                                                        ?.score ??
                                                                        "—"}
                                                                </strong>
                                                            </div>
                                                        </div>
                                                        <div className="insight-food-items">
                                                            {meal.items
                                                                ?.length ? (
                                                                meal.items.map(
                                                                    (item) => (
                                                                        <span
                                                                            key={
                                                                                item.id
                                                                            }
                                                                        >
                                                                            <span>
                                                                                <strong>
                                                                                    {
                                                                                        item.name
                                                                                    }
                                                                                </strong>
                                                                                <small>
                                                                                    {item
                                                                                        .portion
                                                                                        ?.display ||
                                                                                        "Portion unavailable"}
                                                                                </small>
                                                                            </span>
                                                                        </span>
                                                                    ),
                                                                )
                                                            ) : (
                                                                <span>
                                                                    <strong>
                                                                        No
                                                                        recognized
                                                                        items
                                                                    </strong>
                                                                </span>
                                                            )}
                                                        </div>
                                                        {meal.traffic_light
                                                            ?.components && (
                                                            <div className="insight-food-score">
                                                                <div className="insight-food-score-heading">
                                                                    <div>
                                                                        <strong>
                                                                            Food
                                                                            score
                                                                            breakdown
                                                                        </strong>
                                                                        <small>
                                                                            {
                                                                                meal
                                                                                    .traffic_light
                                                                                    .meal_label
                                                                            }
                                                                        </small>
                                                                    </div>
                                                                    <small>
                                                                        {meal
                                                                            .traffic_light
                                                                            .policy_version ===
                                                                        "simple_v2"
                                                                            ? "Simple policy"
                                                                            : "Legacy policy"}
                                                                    </small>
                                                                </div>
                                                                <div className="insight-food-score-grid">
                                                                    {FOOD_SCORE_COMPONENTS.map(
                                                                        ([
                                                                            key,
                                                                            label,
                                                                        ]) => {
                                                                            const component =
                                                                                meal
                                                                                    .traffic_light
                                                                                    ?.components?.[
                                                                                    key
                                                                                ];
                                                                            if (
                                                                                !component
                                                                            )
                                                                                return null;
                                                                            return (
                                                                                <div
                                                                                    className={`insight-food-score-card is-${component.light}`}
                                                                                    key={
                                                                                        key
                                                                                    }
                                                                                >
                                                                                    <div>
                                                                                        <TrafficLight
                                                                                            status={
                                                                                                component.light
                                                                                            }
                                                                                            compact
                                                                                        />
                                                                                        <strong>
                                                                                            {
                                                                                                label
                                                                                            }
                                                                                        </strong>
                                                                                        <b>
                                                                                            {
                                                                                                component.score
                                                                                            }
                                                                                        </b>
                                                                                    </div>
                                                                                    <small>
                                                                                        {
                                                                                            component.meaning
                                                                                        }
                                                                                    </small>
                                                                                    {component.rationale && (
                                                                                        <p>
                                                                                            {
                                                                                                component.rationale
                                                                                            }
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        },
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {meal.traffic_light
                                                            ?.small_improvement && (
                                                            <p className="insight-meal-guidance">
                                                                {
                                                                    meal
                                                                        .traffic_light
                                                                        .small_improvement
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}
                                {dailySummary?.nutrition_totals && (
                                    <div className="insight-metrics">
                                        {Object.entries(
                                            dailySummary.nutrition_totals,
                                        ).map(([key, value]) => (
                                            <span key={key}>
                                                <small>
                                                    {key.replaceAll("_", " ")}
                                                </small>
                                                <strong>
                                                    {formatNutritionValue(
                                                        key,
                                                        value,
                                                    )}
                                                </strong>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </section>
                        )}
                        {!sectionLoading && activeTab === "activity" && (
                            <section className="insight-panel">
                                <ActivityReport userId={userId} days={days} />
                            </section>
                        )}
                        {!sectionLoading && activeTab === "sleep" && (
                            <section className="insight-panel">
                                <SleepReport userId={userId} days={days} />
                            </section>
                        )}
                        {!sectionLoading &&
                            activeTab === "other" &&
                            selectedWeek && (
                                <OtherReports
                                    userId={userId}
                                    weekId={selectedWeek.id}
                                    days={days}
                                />
                            )}
                    </>
                )}
            </div>
        </>
    );
};
